import {InjectionToken} from '@angular/core'
import {AuthEmbeddedService} from '@service/auth/auth.embedded.service'
import {AuthService} from '@service/auth/auth.service'
import type {EmbeddedClientService} from '@service/embeddingService/embedded-client.service'

/**
 * Factory que resuelve la implementación adecuada de `AuthService`.
 *
 * @description
 * Devuelve `AuthEmbeddedService` cuando la app corre embebida en otra
 * aplicación host (detectado por
 * `EmbeddedClientService.isEmbeddedFunction()`) y `AuthService` en modo
 * standalone. El modo embebido delega la autenticación al host a través
 * del canal `postMessage`; el modo normal gestiona el login contra las
 * APIs propias.
 *
 * @param embeddedClientService Cliente que detecta si la app corre embebida.
 * @returns Instancia concreta de `AuthService` o `AuthEmbeddedService`.
 */
export function providerAuthServiceFactory(embeddedClientService: EmbeddedClientService) {
  return embeddedClientService.isEmbeddedFunction() ? new AuthEmbeddedService() : new AuthService()
}

/**
 * `InjectionToken` que referencia la instancia de `AuthService` activa.
 *
 * @description
 * Se registra en `SERVICE_PROVIDERS` junto con {@link providerAuthServiceFactory}.
 * Los consumidores inyectan directamente `AuthService` gracias al alias
 * `{provide: AuthService, useExisting: AuthServiceToken}`; el token existe
 * para poder intercambiar la implementación embebida/normal sin tocar los
 * puntos de inyección.
 *
 * @example
 * ```typescript
 * // en providers.ts
 * {provide: AuthServiceToken, useFactory: providerAuthServiceFactory, deps: [EmbeddedClientService]}
 * {provide: AuthService, useExisting: AuthServiceToken}
 * ```
 */
export const AuthServiceToken = new InjectionToken<AuthService>('AuthService')
